import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Grid, Button, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import '../../simulation-summary/components/summary-bar-chart/index.scss';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import ComparisonBarChart from './comparison-bar-chart';
import { BarChartDetails } from '../../simulation-summary/types/simulation-summary.model';
import { TableComponent as TableType } from '../../pricing-simulator/types/generic-table.model';
import ScenarioListDialog from '../../scenario-list-dialog/scenario-list-dialog';
import {
  getListOfScenarios,
  getScenarioCompMetricsBarCharts,
  handleRemoveScenarioSelection
} from '../store/scenario-comparison-slice';

export default function MetricComparisonBarChartContainer({
  scenarioId,
  selectedScenarioHandler,
  scenarioIndex,
  isUnitSelected
}: {
  scenarioId: number;
  selectedScenarioHandler: (obj) => void;
  scenarioIndex: number;
  isUnitSelected: boolean;
}) {
  const { selectedScenarioArrayWithOrder } = useAppSelector(
    (state) => state.pricingSimulator
  );
  const appDispatch = useAppDispatch();
  const windowWidth = window.innerWidth - window.innerWidth * 0.26;
  const barWidth = windowWidth / 8;
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const sourceId = useAppSelector((state) => state.user.source);
  const [columns, setColumns] = useState([]);
  const metricToggle: boolean = useAppSelector(
    (state) => state.scenarioComparison.metricToggle
  );

  const {
    selectedCompareScenariosList,
    kpisMinMaxMap,
    fetchingTableData,
    inputCols,
    scenarioManagerTableData,
    toggleData,
    isTargetEnabled
  } = useAppSelector((state) => state.scenarioComparison);

  const [preSelectedScenarioIds, setPreselectedScenarioIds] = useState<
    number[]
  >([]);
  const [titleNames, setTitleNames] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState({ key: 0, alias: '' });

  useEffect(() => {
    const filterData = { filter_inputs: [] };
    appDispatch(
      getListOfScenarios({
        filterData,
        sourceId
      })
    );
  }, []);

  useEffect(() => {
    setColumns([
      ...getTableColumnData(inputCols, TableType.SCENARIOMANAGER, [])
    ]);
  }, [inputCols]);

  useEffect(() => {
    const titleArr = getLabelNames();
    setTitleNames([...titleArr]);
  }, [metricToggle, selectedCompareScenariosList]);

  useEffect(() => {
    if (scenarioIndex >= 0) setSelectedIndex({ key: scenarioIndex, alias: '' });
  }, [scenarioIndex]);

  const selectedScenario = (index, scenario) => {
    setSelectedIndex({ key: index, alias: scenario });
    selectedScenarioHandler({ key: index, alias: scenario });
  };

  const handleCloseEventForScenarioList = () => {
    setIsOpenDialog(false);
  };

  const onScenarioLoad = (scenarios: { scenario_id: number }[]) => {
    if (scenarios.length) {
      // const scenarioIds = scenarios.map((scenario) => scenario.scenario_id);
      return appDispatch(
        getScenarioCompMetricsBarCharts({
          sourceId,
          scenariosWithOrderOfSelection: {
            scenarios: selectedScenarioArrayWithOrder
          },
          isTargetEnabled,
        })
      );
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (selectedScenarioArrayWithOrder.length) {
      onScenarioLoad(selectedScenarioArrayWithOrder);
    }
  }, [isTargetEnabled])

  const handleRemoveScenario = (selScenarioId) => {
    const updatedScenarioList = selectedCompareScenariosList.filter(
      (scenario) => scenario.scenario_id !== selScenarioId
    );
    appDispatch(
      handleRemoveScenarioSelection({ updatedList: updatedScenarioList })
    );
  };

  function getLabelNames() {
    let titleArr = [];
    if (
      selectedCompareScenariosList &&
      selectedCompareScenariosList.length > 0
    ) {
      if (!metricToggle) {
        titleArr = selectedCompareScenariosList[0].metrics
          .filter((data) => data.toggle === 'UNITS' || data.toggle === '')
          .map((item) => item.alias);
      } else {
        titleArr = selectedCompareScenariosList[0].metrics
          .filter((data) => data.toggle === 'TONNES' || data.toggle === '')
          .map((item) => item.alias);
      }
    }
    return titleArr;
  }

  const handleDialogOpenEvent = () => {
    setPreselectedScenarioIds(
      selectedCompareScenariosList.map((scenario) => scenario.scenario_id)
    );
    setIsOpenDialog(true);
  };

  // handler for filtering data as per the toggle value
  const getData = (summaryData) => {
    if (toggleData.length) {
      if (!isUnitSelected) {
        return summaryData.metrics.filter(
          (data) => data.toggle === 'UNITS' || data.toggle === ''
        );
      }
      return summaryData.metrics.filter(
        (data) => data.toggle === 'TONNES' || data.toggle === ''
      );
    }
    return summaryData.metrics;
  };

  return (
    <>
      {selectedCompareScenariosList &&
        selectedCompareScenariosList.length > 0 && (
          <Grid
            container
            sx={{
              marginBottom: '10px',
              overflow: 'auto',
              width: '100%',
              border: '1px solid #dcdcdc',
              padding: '0 8px'
            }}
          >
            {/* <Box sx={{ flexGrow: 1 }}> */}
            <Grid
              item
              xs={1.5}
              sm={1.5}
              md={2.5}
              lg={1.5}
              xl={1.5}
              sx={{
                marginTop: '2px',
                position: 'sticky',
                left: '0 !important',
                zIndex: '1'
              }}
            >
              <div style={{ fontSize: '10px', background: '#fff' }}>
                *Numbers indicate change in Abs and % term
              </div>
            </Grid>
            {/* <Grid item xs={10.5} sm={10.5} md={9.5} lg={10.5} xl={10.5} sx={{ borderBottom: "1px solid #dcdcdc" }}> */}
            <Grid item xs={10.5} sm={10.5} md={9.5} lg={10.5} xl={10.5}>
              <div className='master-chart-div'>
                {titleNames.map((title) => (
                  <div
                    className='chart-div'
                    style={{
                      minHeight: 'auto',
                      width: `${140 / titleNames.length}%`
                    }}
                  >
                    <div
                      className='chart-title'
                      style={{ width: `${barWidth + 8}px`, margin: 'auto' }}
                    >
                      {title}
                    </div>
                  </div>
                ))}
              </div>
            </Grid>
            {selectedCompareScenariosList.map((scenario, index) => {
              // getting the filtered data as per the toggle
              const metricComparisonChartData = getData(scenario);
              return (
                <Grid
                  onClick={() =>
                    selectedScenario(index, `Scenario ${scenario.scenario_id}`)
                  }
                  container
                  style={{
                    marginBottom: '10px'
                  }}
                >
                  <Grid
                    item
                    xs={1.5}
                    sm={1.5}
                    md={2.5}
                    lg={1.5}
                    xl={1.5}
                    sx={{
                      background: '#f9f9f9',
                      position: 'sticky',
                      left: '0 !important',
                      zIndex: '1',
                      border:
                        selectedIndex.key === index
                          ? '0.71px solid #0000a0'
                          : '0.71px solid #dcdcdc'
                    }}
                  >
                    <div
                      className='chart-legend-div'
                      title={scenario.scenario_name}
                      style={{
                        marginLeft: '5px',
                        display: 'block'
                      }}
                    >
                      <div
                        className='chart-title-bold'
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        {`Scenario ${index + 1}`}
                        <Typography
                          component='p'
                          fontSize='10px'
                          fontWeight={500}
                        >
                          <div>
                            <IconButton
                              aria-label='close'
                              onClick={(e) =>
                                handleRemoveScenario(scenario.scenario_id)
                              }
                            >
                              <CloseIcon
                                style={{ fontSize: 10, color: '#0000A0' }}
                              />
                            </IconButton>
                          </div>
                        </Typography>
                      </div>
                      <div className='scenario-name-text'>
                        ({scenario.scenario_name})
                      </div>
                    </div>
                  </Grid>
                  <Grid
                    item
                    xs={10.5}
                    sm={10.5}
                    md={9.5}
                    lg={10.5}
                    xl={10.5}
                    sx={{}}
                  >
                    <div className='master-chart-div' style={{}}>
                      {
                        // (scenario.metrics.length && !metricToggle
                        //   ? scenario.metrics.filter(
                        //     (data) => data.toggle === 'UNITS' || data.toggle === ''
                        //   )
                        //   : scenario.metrics.filter(
                        //     (data) => data.toggle === 'TONNES' || data.toggle === ''
                        //   )
                        // ).
                        metricComparisonChartData.map(
                          (chartData: BarChartDetails) => (
                            <ComparisonBarChart
                              chartData={chartData}
                              barWidthValue={barWidth}
                              metricsObject={kpisMinMaxMap}
                              columnCount={metricComparisonChartData.length}
                            />
                          )
                        )
                      }
                    </div>
                  </Grid>
                </Grid>
              );
              // </Grid>
            })}
          </Grid>
          // </Box>
        )}
      <Grid
        container
        sx={{ border: '0.71px solid #dcdcdc', marginBottom: '10px' }}
      >
        <Grid item xs={12}>
          <div
            className='chart-legend-div'
            style={{
              marginLeft: '5px',
              height: '50px',
              background: 'white'
            }}
          >
            <Button
              className='chart-title-bold'
              style={{ color: '#0000a0' }}
              onClick={handleDialogOpenEvent}
              disabled={
                selectedCompareScenariosList &&
                selectedCompareScenariosList.length === 5
              }
            >
              Add Scenario
            </Button>
          </div>
        </Grid>
      </Grid>
      {isOpenDialog ? (
        <ScenarioListDialog
          title='Add Scenario'
          fetchingTableData={fetchingTableData}
          open={isOpenDialog}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onClose={handleCloseEventForScenarioList}
          tableRows={scenarioManagerTableData.row}
          tableColumns={columns}
          footerBtnName='Add Scenario'
          isMultiple
          selectionLimit={5}
          preSelectedScenarioIds={preSelectedScenarioIds}
          onScenarioLoad={onScenarioLoad}
        />
      ) : null}
    </>
  );
}
